import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";
import { CHAPTERS } from "../lib/chapters";
import { SPORT_SHOULDER_CHAPTERS } from "../lib/researchLinks";

const SportsShoulderPage = () => {
  const { lang } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isFr = lang === "fr";

  const title = isFr
    ? "Épaule du sportif à Marseille — Dr Richard Aswad | IMOTEpS"
    : "Sports shoulder in Marseille — Dr Richard Aswad | IMOTEpS";
  const description = isFr
    ? "Spécialiste de l'épaule du sportif à Marseille (13008). Coiffe des rotateurs, instabilité, luxations récidivantes, retour au jeu. Diagnostic, chirurgie arthroscopique, protocole RAAC."
    : "Sports shoulder specialist in Marseille (13008). Rotator cuff, instability, recurrent dislocations, return to play. Diagnosis, arthroscopic surgery, ERAS protocol.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: title,
    description,
    inLanguage: isFr ? "fr-FR" : "en-US",
    about: {
      "@type": "MedicalCondition",
      name: isFr ? "Pathologies de l'épaule du sportif" : "Athlete shoulder conditions",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: isFr ? "Épaule" : "Shoulder" },
    },
    mainEntity: {
      "@type": "Physician",
      name: "Dr Richard Aswad",
      medicalSpecialty: ["Orthopedic", "Sports Medicine"],
      url: "https://imoteps.doctor/",
      telephone: SITE.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "118 Rue Jean Mermoz",
        postalCode: "13008",
        addressLocality: "Marseille",
        addressCountry: "FR",
      },
    },
  };

  const pathologies = [
    { slug: "coiffe-rotateurs", fr: "Rupture de la coiffe des rotateurs", en: "Rotator cuff tear" },
    { slug: "instabilite-epaule", fr: "Instabilité & luxations récidivantes", en: "Instability & recurrent dislocations" },
    { slug: "disjonction-acromio-claviculaire", fr: "Disjonction acromio-claviculaire", en: "AC joint separation" },
    { slug: "tendinopathies-coiffe", fr: "Tendinopathies de la coiffe", en: "Cuff tendinopathies" },
    { slug: "fractures-epaule-clavicule", fr: "Fractures clavicule & épaule", en: "Clavicle & shoulder fractures" },
    { slug: "calcification-epaule", fr: "Calcifications tendineuses", en: "Tendon calcifications" },
  ];

  const sports = ["Rugby", "Tennis", "Handball", "Volley-ball", "Natation", "Crossfit", "Escalade", "Moto", "Motocross", "Judo", "Basketball", "Golf"];

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonical="/epaule-du-sportif"
        keywords="épaule du sportif Marseille, chirurgien épaule sportif, coiffe des rotateurs sportif, instabilité épaule rugby, luxation épaule tennis, retour au sport épaule, Dr Aswad Marseille"
        lang={lang}
        jsonLd={jsonLd}
      />

      <main data-testid="sports-shoulder-page" className="bg-white pt-28 md:pt-36 pb-20">
        <article className="container-page max-w-5xl">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-[#FF5A1F]">{isFr ? "Accueil" : "Home"}</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF5A1F] font-semibold">{isFr ? "Épaule du sportif" : "Sports shoulder"}</span>
          </nav>

          <header className="mt-6">
            <span className="chip">{isFr ? "Traumatologie du sport" : "Sports traumatology"}</span>
            <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
              {isFr
                ? "Épaule du sportif à Marseille — diagnostic, chirurgie, retour au jeu"
                : "Sports shoulder in Marseille — diagnosis, surgery, return to play"}
            </h1>
            <p className="mt-5 text-lg text-foreground/80 leading-relaxed max-w-3xl">
              {isFr
                ? "Le Dr Richard Aswad accompagne sportifs amateurs, compétiteurs et athlètes de haut niveau pour les traumatismes et la chirurgie de l'épaule. Cabinet à la Clinique Juge — Marseille 13008. Plus de 25 ans d'expérience aux côtés de rugbymen, tennismen, nageurs, motards et grimpeurs."
                : "Dr Richard Aswad supports amateur, competitive and elite athletes for shoulder trauma and surgery. Practice at Clinique Juge — Marseille 13008. Over 25 years alongside rugby, tennis, swimming, motorcycle and climbing athletes."}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/#contact" className="btn-primary" data-testid="sports-cta-contact">
                <i className="fa-solid fa-calendar-check" aria-hidden="true"></i>
                {isFr ? "Prendre rendez-vous" : "Book an appointment"}
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost" data-testid="sports-cta-phone">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
            </div>
          </header>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Quand consulter pour une épaule sportive ?" : "When to consult for a sports shoulder?"}
            </h2>
            <div className="mt-5 grid md:grid-cols-2 gap-5">
              {[
                isFr ? "Douleur d'épaule persistante > 3 semaines après une chute ou un effort" : "Shoulder pain persisting > 3 weeks after a fall or effort",
                isFr ? "Sensation d'instabilité, d'« épaule qui sort » lors du geste sportif" : "Sense of instability, shoulder slipping during sport",
                isFr ? "Perte de force au lancer, au service, à l'armer" : "Loss of strength when throwing, serving, drawing back",
                isFr ? "Douleur nocturne empêchant de dormir sur le côté" : "Night pain preventing side-sleeping",
                isFr ? "Récidive d'une luxation déjà connue" : "Recurrence of a known dislocation",
                isFr ? "Échec d'une rééducation bien conduite > 6 semaines" : "Failure of well-conducted rehab > 6 weeks",
              ].map((s, i) => (
                <div key={i} className="flex gap-3 rounded-xl border border-border bg-white p-4">
                  <i className="fa-solid fa-circle-check text-[#FF5A1F] mt-0.5" aria-hidden="true"></i>
                  <p className="text-sm text-foreground/80">{s}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Pathologies traitées chez le sportif" : "Conditions treated in athletes"}
            </h2>
            <p className="mt-3 text-base text-foreground/75 max-w-3xl">
              {isFr
                ? "Chaque pathologie de l'épaule du sportif fait l'objet d'un diagnostic personnalisé et d'une stratégie thérapeutique conservatrice ou chirurgicale, en concertation avec votre médecin du sport et votre kinésithérapeute."
                : "Every athlete shoulder condition receives a personalized diagnosis and a conservative or surgical strategy, in consultation with your sports physician and physiotherapist."}
            </p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pathologies.map((p) => (
                <Link
                  key={p.slug}
                  to={`/pathologie/${p.slug}`}
                  data-testid={`sports-pathology-${p.slug}`}
                  className="group rounded-xl border border-border bg-white p-5 hover:border-[#FF5A1F]/40 hover:shadow-md transition-all"
                >
                  <p className="font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                    {isFr ? p.fr : p.en}
                  </p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#FF5A1F]">
                    {isFr ? "Voir la fiche" : "View details"}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-14 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#102A4C] text-white p-8 md:p-12">
            <h2 className="font-display text-3xl font-bold">
              {isFr ? "Une équipe pour le retour au sport" : "A team for return to sport"}
            </h2>
            <p className="mt-4 text-white/80 max-w-3xl">
              {isFr
                ? "Le Dr Aswad coordonne une prise en charge pluridisciplinaire avec médecins du sport, kinésithérapeutes spécialisés et préparateurs physiques partenaires. Chaque protocole est adapté à votre discipline, votre niveau et vos objectifs de reprise."
                : "Dr Aswad coordinates multidisciplinary care with sports physicians, specialized physiotherapists and partner strength coaches. Every protocol is tailored to your discipline, level and return goals."}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {sports.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white/10 text-xs font-semibold border border-white/15">
                  {s}
                </span>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="font-display text-3xl font-bold text-navy">
              {isFr ? "Délais de reprise — par geste chirurgical" : "Return-to-sport timelines"}
            </h2>
            <div className="mt-5 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-navy text-white text-left">
                  <tr>
                    <th className="px-4 py-3">{isFr ? "Geste" : "Procedure"}</th>
                    <th className="px-4 py-3">{isFr ? "Reprise sport doux" : "Light sport"}</th>
                    <th className="px-4 py-3">{isFr ? "Reprise sport contact / lancer" : "Contact / throwing"}</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {[
                    [isFr ? "Bankart arthroscopique" : "Arthroscopic Bankart", "3 mois", "5–6 mois"],
                    [isFr ? "Latarjet (butée coracoïdienne)" : "Latarjet", "3 mois", "4–5 mois"],
                    [isFr ? "Réparation coiffe arthroscopique" : "Arthroscopic cuff repair", "3–4 mois", "5–6 mois"],
                    [isFr ? "Acromioplastie" : "Acromioplasty", "6 sem.", "3 mois"],
                    [isFr ? "Prothèse anatomique" : "Anatomical prosthesis", "3 mois", "—"],
                  ].map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-3 font-semibold text-navy">{row[0]}</td>
                      <td className="px-4 py-3 text-foreground/75">{row[1]}</td>
                      <td className="px-4 py-3 text-foreground/75">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground italic">
              {isFr
                ? "Délais indicatifs — chaque cas est apprécié individuellement avec le kinésithérapeute et le médecin du sport."
                : "Indicative timelines — every case is assessed individually with the physiotherapist and sports physician."}
            </p>
          </section>

          <section className="mt-14 rounded-2xl bg-white border border-border p-8">
            <h2 className="font-display text-2xl font-bold text-navy">
              {isFr ? "Prendre rendez-vous — Marseille 13008" : "Book — Marseille 13008"}
            </h2>
            <p className="mt-3 text-foreground/75">
              {isFr
                ? "Cabinet à la Clinique Juge — 118 rue Jean Mermoz, 13008 Marseille. Consultations sur rendez-vous, examens d'imagerie sur place, chirurgie programmée et urgences sportives."
                : "Practice at Clinique Juge — 118 rue Jean Mermoz, 13008 Marseille. Appointment consultations, on-site imaging, scheduled surgery and sports emergencies."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href={SITE.phoneHref} className="btn-primary">
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                {SITE.phone}
              </a>
              <Link to="/urgences-epaule" className="btn-ghost">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                {isFr ? "Urgences de l'épaule" : "Shoulder emergencies"}
              </Link>
            </div>
          </section>

          {/* Centre bibliographique cross-link */}
          <section className="mt-16">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">
                  {isFr ? "Centre bibliographique" : "Bibliography center"}
                </p>
                <h2 className="font-display text-3xl font-bold text-navy mt-1">
                  {isFr
                    ? "Pour aller plus loin — chapitres académiques"
                    : "Go deeper — academic chapters"}
                </h2>
              </div>
              <Link to="/recherche" className="hidden md:inline-flex link-underline text-sm">
                {isFr ? "Voir tous les chapitres" : "All chapters"}
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {SPORT_SHOULDER_CHAPTERS.map((cid) => {
                const c = CHAPTERS.find((ch) => ch.id === cid);
                if (!c) return null;
                return (
                  <Link
                    key={c.id}
                    to={`/recherche/chapitre-${c.id}-${c.slug}`}
                    data-testid={`sport-chapter-${c.id}`}
                    className="group rounded-xl bg-white border border-border p-5 hover:shadow-md hover:border-[#FF5A1F]/40 transition-all"
                    style={{ borderTopWidth: 4, borderTopColor: c.color }}
                  >
                    <p
                      className="text-[10px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: c.color }}
                    >
                      {isFr ? `Chapitre ${c.id}` : `Chapter ${c.id}`} · {c.readingMin || 15} min
                    </p>
                    <p className="mt-2 font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
                      {c.title}
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
                      {c.subtitle}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default SportsShoulderPage;
